/** JSON session transport. An ambiguous retry retains its idempotency key and payload. */
export function createLearningClient({ csrf, contextKey, translate, fetchImpl = window.fetch.bind(window), cryptoImpl = window.crypto, storage = window.sessionStorage }) {
  const pendingKeys = new Map();
  async function idempotency(path, payload) {
    const raw = `${contextKey}:${path}:${JSON.stringify(payload)}`;
    if (!cryptoImpl?.subtle) {
      if (!pendingKeys.has(raw)) pendingKeys.set(raw, [...cryptoImpl.getRandomValues(new Uint8Array(24))].map(n => n.toString(16).padStart(2, '0')).join(''));
      return { key: pendingKeys.get(raw), storageKey: raw, memoryOnly: true };
    }
    const digest = await cryptoImpl.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    const storageKey = `learning-write:${[...new Uint8Array(digest)].map(n => n.toString(16).padStart(2, '0')).join('')}`;
    let key = pendingKeys.get(storageKey);
    try { key = key || storage.getItem(storageKey); } catch (_) { /* private browsing */ }
    if (!key) key = [...cryptoImpl.getRandomValues(new Uint8Array(24))].map(n => n.toString(16).padStart(2, '0')).join('');
    pendingKeys.set(storageKey, key);
    try { storage.setItem(storageKey, key); } catch (_) { /* in-memory retries remain stable */ }
    return { key, storageKey };
  }
  return async function request(path, method = 'GET', payload = null, idempotent = false) {
    const headers = { Accept: 'application/json' };
    let pending;
    if (method !== 'GET') { headers['Content-Type'] = 'application/json'; headers['X-CSRF-Token'] = csrf; }
    if (idempotent) { pending = await idempotency(path, payload); headers['Idempotency-Key'] = pending.key; }
    let response; let result;
    try {
      response = await fetchImpl('/api/learning/v1' + path, { method, headers, credentials: 'same-origin', cache: 'no-store', body: payload === null ? undefined : JSON.stringify(payload) });
      result = await response.json();
    } catch (_) { throw new Error(translate('network_error')); }
    if (!response.ok || result.error) {
      const error = new Error(`${response.status === 409 ? translate('conflict') + ' ' : ''}${result.error?.message || translate('error')}${result.meta?.request_id ? ` (${translate('request_id')}: ${result.meta.request_id})` : ''}`);
      error.status = response.status;
      error.fields = result.error?.fields || {};
      throw error;
    }
    if (pending) {
      pendingKeys.delete(pending.storageKey);
      if (!pending.memoryOnly) { try { storage.removeItem(pending.storageKey); } catch (_) { /* unavailable storage */ } }
    }
    return result.data;
  };
}
